import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";

const DEFAULT_COMPETITOR_BRANDS = ["Velo", "Zyn", "Nicotine Pouches - Others"];

const empty = {
  name: "", address: "", latitude: "", longitude: "", radius_m: 200,
  target_units: "", target_conversion: "",
  giveaway_applicable: false,
  giveaway_purchase_min: "",
  giveaway_cases_per_reward: 1,
  giveaway_allow_multiple: false,
  giveaway_competitor_enabled: false,
  giveaway_competitor_min: "",
  giveaway_competitor_cases: 1,
  giveaway_competitor_brands: [...DEFAULT_COMPETITOR_BRANDS],
  giveaway_session_target: "",
  giveaway_cases_supplied: "",
  giveaway_banner: "",
};

export default function AdminStores() {
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [currentProducts, setCurrentProducts] = useState(DEFAULT_COMPETITOR_BRANDS);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stores");
      setStores(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
    api
      .get("/admin/survey-options")
      .then((r) => {
        const cp = r.data?.current_products;
        if (Array.isArray(cp) && cp.length) setCurrentProducts(cp);
      })
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      address: s.address,
      latitude: s.latitude,
      longitude: s.longitude,
      radius_m: s.radius_m,
      target_units: s.target_units ?? "",
      target_conversion: s.target_conversion ?? "",
      giveaway_applicable: !!s.giveaway_applicable,
      giveaway_purchase_min: s.giveaway_purchase_min ?? "",
      giveaway_cases_per_reward: s.giveaway_cases_per_reward ?? 1,
      giveaway_allow_multiple: !!s.giveaway_allow_multiple,
      giveaway_competitor_enabled: !!s.giveaway_competitor_enabled,
      giveaway_competitor_min: s.giveaway_competitor_min ?? "",
      giveaway_competitor_cases: s.giveaway_competitor_cases ?? 1,
      giveaway_competitor_brands: s.giveaway_competitor_brands ?? [...DEFAULT_COMPETITOR_BRANDS],
      giveaway_session_target: s.giveaway_session_target ?? "",
      giveaway_cases_supplied: s.giveaway_cases_supplied ?? "",
      giveaway_banner: s.giveaway_banner ?? "",
    });
    setOpen(true);
  };

  const toggleBrand = (b) => {
    setForm((f) => {
      const has = (f.giveaway_competitor_brands || []).includes(b);
      return {
        ...f,
        giveaway_competitor_brands: has
          ? f.giveaway_competitor_brands.filter((x) => x !== b)
          : [...(f.giveaway_competitor_brands || []), b],
      };
    });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setForm((f) => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        })),
      (err) => toast.error(err.message),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name,
        address: form.address,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        radius_m: parseInt(form.radius_m, 10) || 200,
        target_units: form.target_units === "" ? 0 : parseInt(form.target_units, 10) || 0,
        target_conversion:
          form.target_conversion === "" ? 0 : parseFloat(form.target_conversion) || 0,
        giveaway_applicable: !!form.giveaway_applicable,
        giveaway_purchase_min: form.giveaway_purchase_min === "" ? 0 : parseInt(form.giveaway_purchase_min, 10) || 0,
        giveaway_cases_per_reward: parseInt(form.giveaway_cases_per_reward, 10) || 1,
        giveaway_allow_multiple: !!form.giveaway_allow_multiple,
        giveaway_competitor_enabled: !!form.giveaway_competitor_enabled,
        giveaway_competitor_min: form.giveaway_competitor_min === "" ? 0 : parseInt(form.giveaway_competitor_min, 10) || 0,
        giveaway_competitor_cases: parseInt(form.giveaway_competitor_cases, 10) || 1,
        giveaway_competitor_brands: form.giveaway_competitor_brands || [],
        giveaway_session_target: form.giveaway_session_target === "" ? 0 : parseInt(form.giveaway_session_target, 10) || 0,
        giveaway_banner: form.giveaway_banner || "",
      };
      if (editing) await api.patch(`/admin/stores/${editing.id}`, body);
      else await api.post("/admin/stores", body);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete store ${s.name}?`)) return;
    try {
      await api.delete(`/admin/stores/${s.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white"
          onClick={openCreate}
          data-testid="add-store-button"
        >
          <Plus className="h-4 w-4 mr-1" /> New Store
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 glass-panel">
        {stores.length === 0 && (
          <div className="col-span-full p-6 text-center text-neutral-500">No stores yet.</div>
        )}
        {stores.map((s) => (
          <div
            key={s.id}
            className="glass-panel p-5 -m-px"
            data-testid={`store-card-${s.id}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-lg">{s.name}</div>
                <div className="text-sm text-neutral-500 mt-1">{s.address}</div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-xl h-8 w-8"
                  onClick={() => openEdit(s)}
                  data-testid={`edit-store-${s.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-xl h-8 w-8 text-red-600"
                  onClick={() => del(s)}
                  data-testid={`delete-store-${s.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="font-mono text-xs text-neutral-500 mt-4 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {s.latitude.toFixed(5)}, {s.longitude.toFixed(5)} · {s.radius_m}m
            </div>
            {s.giveaway_applicable && (
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/70 rounded-full px-2.5 py-1" data-testid={`store-giveaway-chip-${s.id}`}>
                🎁 Giveaway enabled
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editing ? "Edit store" : "Create store"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Store name</Label>
              <Input
                className="mt-2 rounded-xl"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="store-form-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Address</Label>
              <Input
                className="mt-2 rounded-xl"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="store-form-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Latitude</Label>
                <Input
                  className="mt-2 rounded-xl font-mono"
                  value={form.latitude}
                  onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                  data-testid="store-form-lat"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Longitude</Label>
                <Input
                  className="mt-2 rounded-xl font-mono"
                  value={form.longitude}
                  onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                  data-testid="store-form-lng"
                />
              </div>
            </div>
            <Button
              variant="outline"
              className="rounded-xl w-full"
              onClick={useMyLocation}
              data-testid="store-form-use-location"
            >
              <MapPin className="h-4 w-4 mr-1" /> Use my current location
            </Button>
            <div>
              <Label className="text-xs uppercase tracking-wider">Geofence radius (meters)</Label>
              <Input
                type="number"
                className="mt-2 rounded-xl"
                value={form.radius_m}
                onChange={(e) => setForm({ ...form, radius_m: e.target.value })}
                data-testid="store-form-radius"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Sales target (units / shift)</Label>
                <Input
                  type="number"
                  min="0"
                  className="mt-2 rounded-xl"
                  placeholder="e.g. 30"
                  value={form.target_units}
                  onChange={(e) => setForm({ ...form, target_units: e.target.value })}
                  data-testid="store-form-target-units"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Conversion target (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-2 rounded-xl"
                  placeholder="e.g. 40"
                  value={form.target_conversion}
                  onChange={(e) => setForm({ ...form, target_conversion: e.target.value })}
                  data-testid="store-form-target-conversion"
                />
              </div>
            </div>
            <p className="text-[11px] text-neutral-400">
              Targets apply to every shift at this store. Leave empty for no target.
            </p>

            {/* ---- Silicone Case Giveaway ---- */}
            <div className="pt-4 mt-2 border-t border-slate-200/70 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-semibold text-sm">Silicone Case Giveaway</div>
                  <div className="text-[11px] text-neutral-400">Reward customers who buy Übbs with a silicone case.</div>
                </div>
                <Switch
                  checked={form.giveaway_applicable}
                  onCheckedChange={(v) => setForm({ ...form, giveaway_applicable: v })}
                  data-testid="store-form-giveaway-applicable"
                />
              </div>

              {form.giveaway_applicable && (
                <div className="space-y-4 rounded-xl bg-white/50 border border-slate-200/60 p-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Purchase rule</Label>
                    <div className="flex items-center flex-wrap gap-2 mt-2 text-sm">
                      <span>Buy</span>
                      <Input
                        type="number" min="1"
                        className="rounded-xl w-20 h-9"
                        value={form.giveaway_purchase_min}
                        onChange={(e) => setForm({ ...form, giveaway_purchase_min: e.target.value })}
                        data-testid="store-form-giveaway-purchase-min"
                      />
                      <span>Übbs cans →  give</span>
                      <Input
                        type="number" min="1"
                        className="rounded-xl w-20 h-9"
                        value={form.giveaway_cases_per_reward}
                        onChange={(e) => setForm({ ...form, giveaway_cases_per_reward: e.target.value })}
                        data-testid="store-form-giveaway-cases-per-reward"
                      />
                      <span>case(s)</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm">Allow multiple rewards</div>
                      <div className="text-[11px] text-neutral-400">Raise the max cases for bigger purchases.</div>
                    </div>
                    <Switch
                      checked={form.giveaway_allow_multiple}
                      onCheckedChange={(v) => setForm({ ...form, giveaway_allow_multiple: v })}
                      data-testid="store-form-giveaway-allow-multiple"
                    />
                  </div>

                  <div className="pt-3 border-t border-slate-200/60">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">Competitor switcher giveaway</div>
                      <Switch
                        checked={form.giveaway_competitor_enabled}
                        onCheckedChange={(v) => setForm({ ...form, giveaway_competitor_enabled: v })}
                        data-testid="store-form-giveaway-competitor-enabled"
                      />
                    </div>
                    {form.giveaway_competitor_enabled && (
                      <div className="mt-3 space-y-3">
                        <div className="flex items-center flex-wrap gap-2 text-sm">
                          <span>Min</span>
                          <Input
                            type="number" min="1"
                            className="rounded-xl w-20 h-9"
                            value={form.giveaway_competitor_min}
                            onChange={(e) => setForm({ ...form, giveaway_competitor_min: e.target.value })}
                            data-testid="store-form-giveaway-competitor-min"
                          />
                          <span>Übbs cans →  give</span>
                          <Input
                            type="number" min="1"
                            className="rounded-xl w-20 h-9"
                            value={form.giveaway_competitor_cases}
                            onChange={(e) => setForm({ ...form, giveaway_competitor_cases: e.target.value })}
                            data-testid="store-form-giveaway-competitor-cases"
                          />
                          <span>case(s)</span>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider">Competing brands (current product)</Label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {currentProducts.map((b) => (
                              <label key={b} className="flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox
                                  checked={(form.giveaway_competitor_brands || []).includes(b)}
                                  onCheckedChange={() => toggleBrand(b)}
                                  data-testid={`store-form-brand-${b}`}
                                />
                                <span>{b}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-3 border-t border-slate-200/60">
                    <Label className="text-xs uppercase tracking-wider">Case target / session</Label>
                    <Input
                      type="number" min="0"
                      className="mt-2 rounded-xl w-40"
                      placeholder="e.g. 10"
                      value={form.giveaway_session_target}
                      onChange={(e) => setForm({ ...form, giveaway_session_target: e.target.value })}
                      data-testid="store-form-giveaway-session-target"
                    />
                    <p className="text-[11px] text-neutral-400 mt-2">
                      Case inventory supplied is set per project in Reports → Inventory (it renews each project).
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider">Banner / instructions</Label>
                    <Textarea
                      className="mt-2 rounded-xl min-h-[70px]"
                      placeholder="Shown to the promoter on the active session screen."
                      value={form.giveaway_banner}
                      onChange={(e) => setForm({ ...form, giveaway_banner: e.target.value })}
                      data-testid="store-form-giveaway-banner"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white"
              disabled={saving}
              onClick={save}
              data-testid="store-form-save"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
